import { createSelector } from "reselect";
import PAGES, { ADMIN_PAGES, SUPERADMIN_PAGES } from "constants/pages";
import _ from "lodash";

export const getActiveUser = (state) => state.activeUser;
export const getAssets = (state) => state.assets;
export const getProcessedAssets = (state) => state.assets;
export const getOldAssets = (state) => state.oldAssets;
export const getCampaigns = (state) => state.campaigns;
export const getNativeResolutions = (state) => state.nativeResolutions;
export const getRequiredResolutions = (state) => state.requiredResolutions;
export const getUsers = (state) => {
  const isAdmin =
    state.activeUser.item.isAdmin ||
    state.activeUser.item.isSuperAdmin ||
    state.activeUser.item.isPowerAdmin;
  return isAdmin ? state.users : [];
};
export const getUserGroups = (state) => state.userGroups;
export const getAgencys = (state) => state.agencys;
export const getPrintSizes = (state) => state.printSizes;
export const getCampaignTableState = (state) => state.campaignTable;
export const getOldAssetsAttachmentState = (state) => state.attachmentProgress;
export const getAssetsTableState = (state) => state.reactTableAssets;
export const getUserManagementTableState = (state) => state.reactTableUserManagement;
export const getDeliveriesTableState = (state) => state.reactTableDeliveries;
export const getProcessPollingStatus = (state) => state.assets.isPollingProcessedItems;

export const getGroupedAssets = createSelector(getAssets, getCampaigns, (assets, campaigns) => {
  const isLoading = campaigns.isLoading || assets.isLoading;
  const error = campaigns.error || assets.error;
  const processedAssets = assets.processedItems;

  if (isLoading || error) return { isLoading, error };

  assets = assets.items.slice(0);

  const groups = campaigns.items.map((campaign) => {
    let groupAssets = [];
    let groupProcessedAssets = [];

    for (let i = 0; i < assets.length; i++) {
      const asset = assets[i];
      if (asset.campaignID === campaign.id) {
        groupAssets.push(asset);

        assets.splice(i, 1);
        i--;
      }
    }
    groupAssets = groupAssets.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    const assetsLength = groupAssets.length;

    for (let i = 0; i < processedAssets.length; i++) {
      const processedAsset = processedAssets[i];
      if (processedAsset.campaignID === campaign.id) {
        if (processedAsset.media && processedAsset.media !== "temp") {
          groupProcessedAssets.push(processedAsset);
        }
      }
    }
    groupProcessedAssets = groupProcessedAssets.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    const processedAssetsLength = groupProcessedAssets.length;
    return {
      campaign: { ...campaign, assetsLength: assetsLength, processedAssetsLength },
      id: campaign.id,
      name: campaign.name,
      updatedAt: campaign.updatedAt,
      assets: groupAssets,
      processedAssets: groupProcessedAssets,
    };
  });

  return { isLoading, error, groups };
});

export const getAssetsByCampaignId = createSelector(
  getGroupedAssets,
  (_, props) => props.campaignId,
  (grouped, campaignId) => {
    if (!grouped || !grouped.groups) return undefined;
    const group = grouped.groups.find((g) => g.id === campaignId);
    return group ? group.assets : undefined;
  }
);

export const getGroupedCampaignById = createSelector(
  getGroupedAssets,
  (_, props) => props.campaignId,
  (grouped, campaignId) => {
    if (!grouped || !grouped.groups) return grouped;
    return grouped.groups.find((g) => g.id === campaignId) || grouped;
  }
);

export const getPages = createSelector(getActiveUser, (user) => {
  const pagesByUserRole = user.item?.isSuperAdmin
    ? SUPERADMIN_PAGES
    : user.item?.isAdmin || user.item?.isPowerAdmin
    ? ADMIN_PAGES
    : PAGES;
  return pagesByUserRole;
});

export const getCampaignLookup = createSelector(getCampaigns, (campaigns) => {
  return campaigns.items.reduce((acc, camp) => {
    acc[camp.id] = camp;
    return acc;
  }, {});
});

// Active native resolutions grouped under their respective required resolutions
export const getGroupedActiveNativeResolutions = createSelector(
  getNativeResolutions,
  getRequiredResolutions,
  (nativeResolutions, requiredResolutions) => {
    const isLoading = requiredResolutions.isLoading || nativeResolutions.isLoading;
    const error = requiredResolutions.error || nativeResolutions.error;

    if (isLoading || error) return { isLoading, error };

    nativeResolutions = nativeResolutions.items.slice(0);

    const groups = requiredResolutions.items.map((requiredResolution) => {
      const groupNativeResolutions = [];

      for (let i = 0; i < nativeResolutions.length; i++) {
        const nativeResolution = nativeResolutions[i];
        if (
          nativeResolution.requiredResolutionID === requiredResolution.id &&
          nativeResolution.active
        ) {
          groupNativeResolutions.push(nativeResolution);
          nativeResolutions.splice(i, 1);
          i--;
        }
      }

      return {
        ...requiredResolution,
        nativeResolutions: groupNativeResolutions,
      };
    });

    // Filter out required resolutions with no active native resolutions
    const groupsWithActiveResolutions = _.filter(groups, function (rr) {
      return rr.nativeResolutions.length > 0;
    });

    return { isLoading, error, groups: groupsWithActiveResolutions };
  }
);
